import { useVirtualizer } from '@tanstack/react-virtual';
import { Checkbox, Tooltip } from '@timelyapp/react';
import classNames from 'classnames';
import type { OptionMeta } from 'components/FilterListItem';
import FilterListItem from 'components/FilterListItem';
import HotKeyTrap from 'components/HotKeyTrap';
import Input from 'components/Input';
import InputDecorator from 'components/InputDecorator';
import Label from 'components/Label';
import type { ReactElement } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import type { AcceptableValues, FilterListItem as FilterListOption } from 'utils/filter-list';
import { makeOptionSelection, siftOptions } from 'utils/filter-list';
import Avatar from './Avatar';
import DiagnosticResourceId from './dev-tools/DiagnosticResourceId';
import styles from './FilterList.module.scss';

type AcceptableValue = any; // number|string

interface GroupFilter {
  value: AcceptableValue;
  name: string;
}

type TypicalEvent =
  | React.KeyboardEvent<HTMLInputElement>
  | React.KeyboardEvent<HTMLElement>
  | React.MouseEvent<HTMLElement>
  | KeyboardEvent;

interface Props {
  resource?: string;
  hideFromFullstory?: boolean;
  labelBatchAll?: string;
  labelBatchNone?: string;
  values: Array<AcceptableValue>;
  renderOption?: (option: Partial<FilterListOption>, optionMeta: OptionMeta) => React.ReactElement;
  options: Array<FilterListOption>;
  searchable?: boolean;
  placeholder?: string;
  groupFilter?: string | null;
  groupFilters?: Array<GroupFilter> | null | false;
  onChange: (
    filterListOption: FilterListOption,
    a: Array<AcceptableValue>,
    typicalEvent: TypicalEvent
  ) => void;
  onChangeGroup?: (str: string, a: Array<AcceptableValue>) => void;
  onBatchSelect?: (str: string, a: Array<AcceptableValue>) => void;
  ulClassName?: string;
  maxScrollHeight?: number | string | false | null;
  minScrollHeight?: number | string;
  allowBatchSelect?: boolean;
  singleSelect?: boolean;
  singleChildSelect?: boolean; // No limit on toplevel options, only 1 child allowed
  animated?: boolean;
  includeAllChildrenIfNoneMatch?: boolean;
  onSearch?: (searchString: string) => void;
  hideNoneBatchOption?: boolean;
  hideBatchOptions?: boolean;
  extraMeta?: Record<AcceptableValue, Partial<OptionMeta>>;
  /** Enable virtualized rendering for large lists using @tanstack/react-virtual */
  virtualized?: boolean;
}

const ITEM_HEIGHT = 36;

const FilterList: React.FC<Props> = ({
  resource,
  hideFromFullstory = false,
  labelBatchAll = 'All',
  labelBatchNone = 'None',
  values = [],
  renderOption: renderOptionProp,
  options = [],
  searchable = true,
  placeholder = 'Search',
  groupFilter: groupFilterProp = null,
  groupFilters,
  onChange,
  onChangeGroup,
  onBatchSelect,
  ulClassName: _ulClassName,
  maxScrollHeight = null,
  minScrollHeight,
  singleSelect = false,
  singleChildSelect = false,
  animated = true,
  includeAllChildrenIfNoneMatch = false,
  onSearch,
  hideNoneBatchOption,
  hideBatchOptions,
  extraMeta,
  virtualized = false,
}) => {
  const [filter, setFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState<string | null>(groupFilterProp);
  const [focusValue, setFocusValue] = useState<AcceptableValue | null>(() => {
    const siftedOptions = siftOptions(options);
    return siftedOptions.length > 0 ? siftedOptions[0].value : null;
  });

  const filterInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Sync groupFilter prop
  useEffect(() => {
    if (groupFilterProp !== groupFilter) {
      setGroupFilter(groupFilterProp);
    }
  }, [groupFilterProp, groupFilter]);

  // Memoized flat filtered options -- used for keyboard navigation (always) and virtualized rendering
  const filteredOptions = useMemo(() => {
    return siftOptions(options, {
      filterString: filter,
      flattenArray: true,
      groupFilter,
      includeAllChildrenIfNoneMatch,
    });
  }, [options, filter, groupFilter, includeAllChildrenIfNoneMatch]);

  // Set up virtualizer -- always called (hooks can't be conditional), but count=0 when not virtualized
  const rowVirtualizer = useVirtualizer({
    count: virtualized ? filteredOptions.length : 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 10,
  });

  // Update focus value when options change (e.g. options received for the first time)
  useEffect(() => {
    if (filteredOptions.length > 0 && focusValue === null) {
      setFocusValue(filteredOptions[0].value);
    }
  }, [filteredOptions, focusValue]);

  const handleSetFocus = useCallback(() => {
    if (searchable && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [searchable]);

  // Focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSetFocus();
    }, 25);
    return () => clearTimeout(timer);
  }, [handleSetFocus]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const filterString = event.target.value;
      setFilter(filterString);

      const newOptions = siftOptions(options, {
        filterString,
        flattenArray: true,
        groupFilter,
      });
      setFocusValue(newOptions[0]?.value ?? null);

      onSearch?.(filterString);
    },
    [options, groupFilter, onSearch]
  );

  const handleClearFilter = useCallback(() => {
    setFilter('');
    handleSetFocus();
  }, [handleSetFocus]);

  // Clicking an option should select it.
  const handleOptionClick = useCallback(
    (event: TypicalEvent, itemProps: FilterListOption) => {
      if (!itemProps) return;

      if (onChange) {
        const newValues = makeOptionSelection(itemProps, {
          options,
          values,
          singleSelect,
          singleChildSelect,
        });
        onChange(itemProps, newValues, event);
      }

      setFocusValue(itemProps.value);
      handleSetFocus();
    },
    [onChange, options, values, singleSelect, singleChildSelect, handleSetFocus]
  );

  // Hovering over an option should set it as focused option:
  const handleMouseEnterOption = useCallback(
    (event: React.MouseEvent<HTMLElement>, itemProps: FilterListOption) => {
      if (itemProps) {
        setFocusValue(itemProps.value);
      }
      handleSetFocus();
    },
    [handleSetFocus]
  );

  const handleSetGroupFilter = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const filterValue = event.currentTarget.getAttribute('data-group-filter-value') ?? '';
      setGroupFilter(filterValue);

      // Pass up all options that match the current filter & grouping:
      const siftedOptions = siftOptions(options, {
        filterString: '',
        flattenArray: true,
        groupFilter: filterValue,
      });

      const optionValues = siftedOptions.map(item => item.value);
      handleSetFocus();
      onChangeGroup?.(filterValue, optionValues);
    },
    [options, handleSetFocus, onChangeGroup]
  );

  // Selecting all | none entries:
  const handleOnBatchSelect = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const batch = event.currentTarget.getAttribute('data-batch') ?? '';
      handleSetFocus();
      let batchOptions: Array<AcceptableValues> = [];

      // Find all options in the current groupFilter
      if (batch === 'all') {
        const siftedOptions = siftOptions(options, {
          filterString: '',
          flattenArray: true,
          groupFilter,
        });
        batchOptions = siftedOptions.map(item => item.value);
      }

      onBatchSelect?.(batch, batchOptions);
    },
    [options, groupFilter, handleSetFocus, onBatchSelect]
  );

  // When FilterList is inside a <FORM>, ENTER is interpreted as clicking the submit button,
  // and the event would by-pass handleOnKey. This hackish workaround solves that.
  const handleInterceptKeyPress = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }, []);

  const handleOnKey = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        event.stopPropagation();
      }

      const filterInput = filterInputRef.current;
      const globalFocusedEl = document.activeElement;

      // If we're not focused on input, abort: We assume we're not interacting
      // with the FilterList. Likely needs to be more specific (multiple lists, generic name+++)
      if (
        filterInput &&
        filterInput.getAttribute('name') !== globalFocusedEl?.getAttribute('name')
      ) {
        return;
      }

      const currentIndex = filteredOptions.findIndex(o => o.value === focusValue);
      const prevItem = filteredOptions[currentIndex - 1];
      const nextItem = filteredOptions[currentIndex + 1];

      switch (event.key) {
        case 'ArrowUp': {
          if (prevItem || filteredOptions.length > 0) {
            const newValue = prevItem
              ? prevItem.value
              : filteredOptions[filteredOptions.length - 1].value;
            setFocusValue(newValue);
            // Scroll to the focused item when virtualized
            if (virtualized) {
              const newIndex = prevItem ? currentIndex - 1 : filteredOptions.length - 1;
              rowVirtualizer.scrollToIndex(newIndex, { align: 'auto' });
            }
          }
          handleSetFocus();
          break;
        }
        case 'ArrowDown': {
          if (nextItem || filteredOptions.length > 0) {
            const newValue = nextItem ? nextItem.value : filteredOptions[0].value;
            setFocusValue(newValue);
            // Scroll to the focused item when virtualized
            if (virtualized) {
              const newIndex = nextItem ? currentIndex + 1 : 0;
              rowVirtualizer.scrollToIndex(newIndex, { align: 'auto' });
            }
          }
          handleSetFocus();
          break;
        }
        case 'Enter': {
          const current = filteredOptions.find(o => o.value === focusValue);
          if (!current) return;
          handleOptionClick(event, current);
          break;
        }
        default:
          break;
      }
    },
    [filteredOptions, focusValue, handleSetFocus, handleOptionClick, virtualized, rowVirtualizer]
  );

  // Default render option -- preserves Archived/Required labels from the original FilterList
  const defaultRenderOption = useCallback(
    (option: FilterListOption, meta: OptionMeta) => {
      const showCheckbox =
        (!singleSelect && !singleChildSelect) || (singleChildSelect && !option.parent_id);
      const showRadio = singleChildSelect && option.parent_id;

      return (
        <div className={hideFromFullstory ? 'fs-mask' : undefined}>
          {option.active === false ? (
            <Label
              size="small"
              variant={meta.isSelected ? 'muted' : 'muted-danger'}
              className={styles.required}
            >
              Archived
            </Label>
          ) : null}
          {showCheckbox ? (
            <Checkbox
              className={styles.checkbox}
              tabIndex={-1}
              readOnly
              disabled={meta.isDisabled}
              checked={meta.isSelected}
            />
          ) : null}
          {showRadio ? (
            <input
              type="radio"
              tabIndex={-1}
              readOnly
              disabled={meta.isDisabled}
              checked={meta.isSelected}
            />
          ) : null}
          {option.imageIsAvatar ? (
            <Avatar
              tooltip={false}
              name={option.name}
              size="small"
              shape="circle"
              src={option.image}
              fallbackToInitials
              className={classNames(styles.avatar, { 'fs-exclude': hideFromFullstory })}
            />
          ) : (
            option.image && (
              <img src={option.image} className={hideFromFullstory ? 'fs-exclude' : undefined} />
            )
          )}
          {resource != null && <DiagnosticResourceId resource={resource} id={option.value} />}
          <Tooltip tooltip={option.name} position="right">
            <span>{option.name}</span>
          </Tooltip>
          {!!(meta.hint || meta.hintTooltip) && (
            <span className={styles.hint}>
              {meta.hint}
              {meta.hintTooltip}
            </span>
          )}
          {option.required ? (
            <Label
              size="small"
              variant={meta.isSelected ? 'muted' : 'muted-danger'}
              className={styles.required}
            >
              Required
            </Label>
          ) : null}
        </div>
      );
    },
    [singleSelect, singleChildSelect, hideFromFullstory, resource]
  );

  const renderOption = renderOptionProp || defaultRenderOption;

  // Hierarchical rendering for non-virtualized mode (supports parent/child nesting)
  const renderHierarchicalOptions = () => {
    const siftSettings = {
      filterString: filter,
      groupFilter,
      includeAllChildrenIfNoneMatch,
    };

    return siftOptions(options, siftSettings).map(option => {
      let children: Array<ReactElement> = [];
      if (!!option.children && option.children.length) {
        let optionChildren;
        if (!includeAllChildrenIfNoneMatch && !!option.children) {
          optionChildren = siftOptions(option.children, siftSettings);
        } else if (option.children) {
          optionChildren = option.children;
        }

        children =
          optionChildren?.map(childOption => {
            // 2nd level option
            const isSelected = values.includes(childOption.value);
            const isInFocus = childOption.value === focusValue;
            return (
              <FilterListItem
                key={`child-${childOption.value}`}
                onClick={handleOptionClick}
                onMouseEnter={handleMouseEnterOption}
                isSelected={!!isSelected}
                isInFocus={isInFocus}
                item={childOption}
                renderMethod={renderOption}
                singleSelect={singleSelect}
              />
            );
          }) ?? [];
      }
      // 1st level option
      const isSelected = values.includes(option.value);
      const isInFocus = option.value === focusValue;

      const itemExtraMeta = option.value != null ? extraMeta?.[option.value] : undefined;

      return (
        <FilterListItem
          key={`parent-${option.value}`}
          onClick={handleOptionClick}
          onMouseEnter={handleMouseEnterOption}
          isSelected={!!isSelected}
          isInFocus={isInFocus}
          isDisabled={itemExtraMeta?.isDisabled}
          item={option}
          renderMethod={renderOption}
          subItems={children}
          singleSelect={singleSelect}
          hint={itemExtraMeta?.hint}
          hintTooltip={itemExtraMeta?.hintTooltip}
        />
      );
    });
  };

  const renderGroupFilters = () => {
    if (!groupFilters) return null;

    return (
      <div className={styles.groupFilters}>
        {groupFilters.map(gf => (
          <button
            className={groupFilter === gf.value ? styles.active : undefined}
            key={gf.value}
            data-group-filter-value={gf.value}
            onClick={handleSetGroupFilter}
            type="button"
          >
            {gf.name}
          </button>
        ))}
      </div>
    );
  };

  // Compute scroll heights
  const scrollHeight =
    maxScrollHeight === false
      ? 999999
      : typeof maxScrollHeight === 'number'
        ? maxScrollHeight
        : typeof maxScrollHeight === 'string'
          ? parseInt(maxScrollHeight, 10) || 400
          : document.documentElement.clientHeight - 350;

  const minHeight = virtualized
    ? typeof minScrollHeight === 'number'
      ? minScrollHeight
      : typeof minScrollHeight === 'string'
        ? parseInt(minScrollHeight, 10) || 400
        : 400
    : undefined;

  const hasQueryFilter = !!filter.length;
  const showBatchActions = !singleSelect && onBatchSelect && !hasQueryFilter && !hideBatchOptions;

  // Render the list content
  const renderContent = () => {
    if (virtualized) {
      return (
        <ul
          className={styles.ul}
          data-testid="filter-list"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map(virtualItem => {
            const option = filteredOptions[virtualItem.index];
            const isSelected = values.includes(option.value);
            const isInFocus = option.value === focusValue;
            const itemExtraMeta = option.value != null ? extraMeta?.[option.value] : undefined;

            return (
              <li
                key={virtualItem.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <FilterListItem
                  onClick={handleOptionClick}
                  onMouseEnter={handleMouseEnterOption}
                  isSelected={isSelected}
                  isInFocus={isInFocus}
                  isDisabled={itemExtraMeta?.isDisabled}
                  item={option}
                  renderMethod={renderOption}
                  singleSelect={singleSelect}
                  hint={itemExtraMeta?.hint}
                  hintTooltip={itemExtraMeta?.hintTooltip}
                />
              </li>
            );
          })}
        </ul>
      );
    }

    // Non-virtualized: hierarchical rendering
    const optionElements = renderHierarchicalOptions();

    if (!animated) {
      return (
        <ul className={styles.ul} data-testid="filter-list">
          {optionElements}
        </ul>
      );
    }
    return <ul className={styles.ul}>{optionElements}</ul>;
  };

  // Render scroll wrapper
  const renderScrollWrapper = () => {
    if (virtualized) {
      return (
        <div
          ref={scrollContainerRef}
          style={{
            maxHeight: scrollHeight,
            minHeight: minHeight,
            overflow: 'auto',
          }}
        >
          {renderContent()}
        </div>
      );
    }

    return (
      <Scrollbars autoHeight autoHeightMax={scrollHeight} renderTrackHorizontal={() => <div />}>
        {renderContent()}
      </Scrollbars>
    );
  };

  return (
    <div className={styles.container} onKeyPress={handleInterceptKeyPress}>
      <HotKeyTrap onKey={handleOnKey} />
      {renderGroupFilters()}
      {searchable ? (
        <div className={styles.search}>
          {showBatchActions ? (
            <div className={styles.batchActions} style={{ right: filter ? 30 : 10 }}>
              <button
                data-testid="all_tags_filter_list"
                type="button"
                data-batch="all"
                onClick={handleOnBatchSelect}
                tabIndex={-1}
                style={{ cursor: 'pointer' }}
              >
                {labelBatchAll}
              </button>
              {!hideNoneBatchOption && (
                <button
                  data-testid="none_tags_filter_list"
                  type="button"
                  data-batch="none"
                  onClick={handleOnBatchSelect}
                  tabIndex={-1}
                  style={{ cursor: 'pointer' }}
                >
                  {labelBatchNone}
                </button>
              )}
            </div>
          ) : null}
          <InputDecorator faIcon="search" isClearable={hasQueryFilter} onClear={handleClearFilter}>
            <Input
              autoComplete="off"
              autoFocus
              name="filter"
              ref={filterInputRef}
              value={filter}
              onChange={handleInputChange}
              size="medium"
              placeholder={placeholder}
            />
          </InputDecorator>
        </div>
      ) : null}
      {renderScrollWrapper()}
    </div>
  );
};

export default FilterList;
